import React, { useState } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Button, 
  Card, 
  CardContent, 
  Divider,
  CircularProgress,
  Collapse,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

const MobileCampView = ({ camps, loading, error, fetchCamps }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State to track which camp is expanded
  const [expandedCamp, setExpandedCamp] = useState(null);
  
  // Toggle expanded state of a camp
  const toggleCampExpand = (campHandle) => {
    if (expandedCamp === campHandle) {
      setExpandedCamp(null);
    } else {
      setExpandedCamp(campHandle);
    }
  };
  
  // Close expanded camp view
  const closeDates = () => {
    setExpandedCamp(null);
  };

  if (loading) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Loading camps...
      </Typography>
      <CircularProgress />
    </Container>
  );

  if (error) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" color="error" sx={{ mb: 2 }}>
        {error}
      </Typography>
      <Button 
        variant="contained" 
        onClick={() => fetchCamps()}
        sx={{ mt: 2 }}
      >
        Retry
      </Button>
    </Container>
  );

  if (!camps.length) return (
    <Container sx={{ mt: 4, textAlign: 'center' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        No camps available at this time
      </Typography>
      <Button 
        variant="contained" 
        onClick={() => fetchCamps()}
        sx={{ mt: 2 }}
      >
        Refresh
      </Button>
    </Container>
  );

  return (
    <Container maxWidth="sm" sx={{ py: 2 }}>
      <Typography variant="h4" component="h1" align="center" gutterBottom>
        Summer Camps 2025
      </Typography>
      
      {camps.map((camp) => (
        <Card 
          key={camp.handle} 
          sx={{ 
            mb: 2, 
            borderRadius: 2,
            boxShadow: expandedCamp === camp.handle ? 3 : 1,
            transition: 'all 0.3s ease',
            border: expandedCamp === camp.handle ? `2px solid ${theme.palette.primary.main}` : 'none'
          }}
        >
          {/* Camp Header - Always visible */}
          <CardContent 
            sx={{ 
              p: 2, 
              '&:last-child': { pb: 2 },
              cursor: 'pointer',
              display: 'flex',
              flexDirection: 'column'
            }}
            onClick={() => toggleCampExpand(camp.handle)}
          >
            <Box sx={{ display: 'flex', mb: 1 }}>
              {camp.imageUrl && (
                <Box 
                  component="img"
                  src={camp.imageUrl}
                  alt={camp.imageAlt}
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: 1,
                    mr: 2,
                    objectFit: 'cover'
                  }}
                />
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                  {camp.title}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Ages: 10-12 • {camp.tags.includes('beginner') ? 'Beg-Int' : 'Beg-Adv'}
                </Typography>
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        window.open(`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${camp.handle}`, '_blank');
                      }}
                      sx={{ 
                        borderRadius: 4,
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        py: 0.5,
                        width: '100%'
                      }}
                    >
                      Course info
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      variant="contained"
                      size="small"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleCampExpand(camp.handle);
                      }}
                      sx={{ 
                        borderRadius: 4,
                        textTransform: 'none',
                        fontSize: '0.8rem',
                        py: 0.5,
                        bgcolor: '#8BC34A',
                        '&:hover': { bgcolor: '#7CB342' },
                        width: '100%'
                      }}
                    >
                      Select Dates
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </CardContent>
          
          {/* Expanded Camp Details */}
          <Collapse in={expandedCamp === camp.handle}>
            <Divider />
            <CardContent sx={{ p: 2, bgcolor: 'rgba(0, 0, 0, 0.02)' }}>
              {/* Header with Close Button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Available Dates</Typography>
                <IconButton 
                  size="small" 
                  onClick={closeDates}
                  sx={{ 
                    bgcolor: 'rgba(0, 0, 0, 0.05)',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.1)' }
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
              
              {/* Available Dates */}
              {camp.weeks.filter(week => week.available).map((week) => {
                // Determine if this week has limited spots
                const hasLimitedSpots = week.limitedSpots;
                const spotStatus = hasLimitedSpots ? `${week.spotsLeft} left` : 'Open';
                const statusColor = hasLimitedSpots ? '#ff9800' : '#4caf50';
                
                return (
                  <Card 
                    key={week.week} 
                    variant="outlined" 
                    sx={{ 
                      mb: 1.5,
                      borderColor: hasLimitedSpots ? '#ff9800' : '#e0e0e0',
                      borderRadius: 2
                    }}
                  >
                    <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {week.month} {week.dates}
                          </Typography>
                          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                            <Box 
                              sx={{ 
                                width: 10, 
                                height: 10, 
                                borderRadius: '50%', 
                                bgcolor: statusColor,
                                mr: 1
                              }} 
                            />
                            <Typography variant="body2" color="text.secondary">
                              {spotStatus}
                            </Typography>
                          </Box>
                        </Box>
                        <Button 
                          variant="contained" 
                          color="primary"
                          size="small"
                          sx={{ 
                            borderRadius: 4,
                            px: 2,
                            py: 0.5,
                            textTransform: 'none',
                            bgcolor: '#8BC34A',
                            '&:hover': { bgcolor: '#7CB342' }
                          }}
                          onClick={() => window.open(`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${camp.handle}`, '_blank')}
                        >
                          Select Date
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
              
              {camp.weeks.filter(week => week.available).length === 0 && (
                <Box sx={{ p: 2, textAlign: 'center', bgcolor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="body1" color="text.secondary">
                    No dates currently available
                  </Typography>
                  <Button 
                    variant="outlined" 
                    sx={{ mt: 1.5, borderRadius: 4, textTransform: 'none' }}
                    onClick={() => window.open(`https://${process.env.REACT_APP_SHOPIFY_STORE_URL}/products/${camp.handle}`, '_blank')}
                  >
                    Try online
                  </Button>
                </Box>
              )}
            </CardContent>
          </Collapse>
        </Card>
      ))}
    </Container>
  );
};

export default MobileCampView;
